import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, List, Table, TableBody, TableCell, TableHead, TableRow, Chip, Alert } from '@mui/material';
import { ExpandMore, Code, BugReport } from '@mui/icons-material';
import { SectionHeader, Done, Todo } from './DocPrimitives';
import { TECH_STACK, RIDE_LIFECYCLE_STATES, RIDE_LIFECYCLE_DONE, OFFLINE_DONE, OFFLINE_TODO, SECURITY_DONE, SECURITY_TODO, KNOWN_GAPS } from '../../data/docs/technical';

export default function TechnicalTab() {
  return (
    <Box>
      <SectionHeader icon={<Code />} title="Technical Documentation" subtitle="Architecture decisions, data models, and system design" />

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Technology Stack</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Layer</strong></TableCell>
                <TableCell><strong>Technology</strong></TableCell>
                <TableCell><strong>Notes</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TECH_STACK.map(({ layer, technology, notes }) => (
                <TableRow key={layer}>
                  <TableCell><strong>{layer}</strong></TableCell>
                  <TableCell><Typography variant="body2">{technology}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{notes}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Ride Lifecycle</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" mb={2}>
            A ride request moves through these states. Each transition is a GraphQL mutation that triggers a subscription event to the relevant rider and driver.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {RIDE_LIFECYCLE_STATES.map((s, i) => (
              i < RIDE_LIFECYCLE_STATES.length - 1
                ? [<Chip key={s} label={s} size="small" variant="outlined" />, <Typography key={`arr-${i}`} variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>→</Typography>]
                : <Chip key={s} label={s} size="small" variant="outlined" />
            ))}
          </Box>
          <List dense disablePadding>
            {RIDE_LIFECYCLE_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Offline Resilience</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {OFFLINE_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {OFFLINE_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Security Controls</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List dense disablePadding>
            {SECURITY_DONE.map((item, i) => <Done key={i}>{item}</Done>)}
            {SECURITY_TODO.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <BugReport fontSize="small" />
            <Typography fontWeight={600}>Known Gaps & Planned Work</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">These items are tracked and planned. They are documented here so reviewers and testers have full visibility.</Typography>
          </Alert>
          <List dense disablePadding>
            {KNOWN_GAPS.map((item, i) => <Todo key={i}>{item}</Todo>)}
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
